import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";

import DstAlert from "../../../components/DstAlert/DstAlert";
import DstButton from "../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../components/Popup/Popup";
import RequiredLabel from "../../../components/RequiredLabel/RequiredLabel";
import { EvaluationTypeEnum } from "../../../models/EvaluationTypes";
import { LanguageEnum } from "../../../models/Language";
import { ProgrammingLanguageEnum } from "../../../models/ProgrammingLanguage";
import { validateUserPermission } from "../../../services/PermissionGuard";
import translate from "../../../services/Translate";
import { EvaluationCreationModalProps } from "./CreationModal.d";
import {
  createEvaluation,
  fetchPrograms,
  getAcceptedSubjectExtensions,
  isValidSubjectFile,
} from "./CreationModal.function";

const EvaluationCreationModal = ({
  language,
  permissions,
  setShowCreationModal,
  showCreationModal,
}: EvaluationCreationModalProps) => {
  const [loading, setLoading] = useState<{
    creation: boolean;
    programs: boolean;
  }>({
    creation: false,
    programs: true,
  });
  const [programs, setPrograms] = useState<any[]>([]);
  const [owner, setOwner] = useState<any | null>(null);
  const [selectedModule, setSelectedModule] = useState<any | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<any | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [fileInputKey, setFileInputKey] = useState(0);

  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<any>({});

  const _clearFile = () => {
    setFile(null);
    setFileError("");
    setFileInputKey((prev) => prev + 1);
    setForm((prev) => ({ ...prev, s3_name: "" }));
  };

  const _resetSelection = () => {
    setForm({
      name: "",
      description: "",
      s3_name: "",
      duration: 60,
      language: "",
      programming_language: "",
      type: "",
      is_deprecated: false,
      allow_repass: true,
      success_ratio: "",
    });
    setSelectedModule(null);
    setSelectedProgram(null);
    setOwner(null);
    setFile(null);
    setFileError("");
    setFileInputKey((prev) => prev + 1);
  };

  const [form, setForm] = useState({
    name: "",
    description: "",
    s3_name: "",
    duration: 60,
    language: "",
    programming_language: "",
    type: "",
    is_deprecated: false,
    allow_repass: true,
    success_ratio: "",
  });

  const _isFormInvalid =
    loading.programs ||
    !form.name.trim() ||
    !form.description.trim() ||
    !file ||
    !form.s3_name.trim() ||
    Number(form.duration) < 1 ||
    !form.language ||
    !form.programming_language.trim() ||
    !form.type.trim() ||
    !owner?.id ||
    (form.success_ratio !== "" && (Number(form.success_ratio) < 0 || Number(form.success_ratio) > 1));

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === "type" && file && !isValidSubjectFile(file, value)) {
      _clearFile();
    }
  };

  const _handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    if (!selectedFile) {
      _clearFile();
      return;
    }

    if (form.type && !isValidSubjectFile(selectedFile, form.type)) {
      setFileError(translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.ERRORS.INVALID_FILE"));
      setFile(null);
      setFileInputKey((prev) => prev + 1);
      setForm((prev) => ({ ...prev, s3_name: "" }));
      return;
    }

    setFile(selectedFile);
    setFileError("");
    setForm((prev) => ({
      ...prev,
      s3_name: selectedFile.name.replace(/\.[^/.]+$/, ""),
    }));
  };

  const displayAlert = (props: any) => {
    setAlertProps(props);
    setShowAlert(true);
  };

  const _handleAlertClose = () => {
    setShowAlert(false);
    _resetSelection();
  };

  const _handleSubmit = () => {
    if (!file) {
      setFileError(translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.ERRORS.FILE_REQUIRED"));
      return;
    }

    if (form.type && !isValidSubjectFile(file, form.type)) {
      setFileError(translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.ERRORS.INVALID_FILE"));
      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description,
      s3_name: form.s3_name.trim(),
      duration: Number(form.duration),
      language: form.language,
      programming_language: form.programming_language.trim(),
      type: form.type.trim(),
      is_deprecated: form.is_deprecated,
      allow_repass: form.allow_repass,
      owner_id: owner.id,
      module_id: selectedModule?.id ?? null,
      program_id: selectedProgram?.id ?? null,
      success_ratio: form.success_ratio === "" ? null : Number(form.success_ratio),
    };
    createEvaluation(
      payload,
      language,
      (value: boolean) => setLoading((prev) => ({ ...prev, creation: value })),
      displayAlert,
      setShowCreationModal,
      _resetSelection,
      file
    );
  };

  useEffect(() => {
    if (showCreationModal && validateUserPermission([{ route: "/programs", methods: ["GET"] }], permissions)) {
      fetchPrograms(language, setPrograms, (value: boolean) => setLoading((prev) => ({ ...prev, programs: value })));
    }
  }, [language, showCreationModal, permissions]);

  const _programItems = useMemo(() => programs.map((program) => ({ id: program.id, name: program.name })), [programs]);

  return (
    <React.Fragment>
      <Popup
        show={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        modalTitle={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.TITLE")}
        scrollable
      >
        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.NAME")} />
          <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.DESCRIPTION")} />
          <Form.Control
            as="textarea"
            rows={3}
            value={form.description}
            onChange={(event) => _handleChange("description", event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.FILE")} />
          <Form.Control
            key={fileInputKey}
            type="file"
            accept={getAcceptedSubjectExtensions(form.type)}
            onChange={_handleFileChange}
            className="mt-1"
          />
          <Form.Text muted>{translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PLACEHOLDERS.FILE_HINT")}</Form.Text>
          {fileError && <Form.Text className="text-danger d-block">{fileError}</Form.Text>}
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.DURATION")} />
          <Form.Control
            type="number"
            min={1}
            value={form.duration}
            onChange={(event) => _handleChange("duration", Number(event.target.value))}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.LANGUAGE")} />
          <Form.Select value={form.language} onChange={(event) => _handleChange("language", event.target.value)}>
            <option value="" disabled>
              {translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PLACEHOLDERS.SELECT_LANGUAGE")}
            </option>

            {Object.values(LanguageEnum).map((languageCode) => (
              <option key={languageCode} value={languageCode}>
                {languageCode}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PROGRAMMING_LANGUAGE")} />
          <Form.Select
            value={form.programming_language}
            onChange={(event) => _handleChange("programming_language", event.target.value)}
          >
            <option value="" disabled>
              {translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PLACEHOLDERS.SELECT_PROGRAMMING_LANGUAGE")}
            </option>

            {Object.values(ProgrammingLanguageEnum).map((programmingLanguage) => (
              <option key={programmingLanguage} value={programmingLanguage}>
                {programmingLanguage}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.TYPE")} />
          <Form.Select value={form.type} onChange={(event) => _handleChange("type", event.target.value)}>
            <option value="" disabled>
              {translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PLACEHOLDERS.SELECT_TYPE")}
            </option>

            {Object.values(EvaluationTypeEnum).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.OWNER")} />
          <MultiSelectLiveDropdown
            category={["users"]}
            language={language}
            isSingleSelect
            label={
              owner
                ? `${owner.first_name} ${owner.last_name}`
                : translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PLACEHOLDERS.SELECT_OWNER")
            }
            toggleClass="w-100 border"
            onConfirm={(selectedUsers: any[]) => setOwner(selectedUsers.length ? selectedUsers[0].value : null)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          {translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.CORRECTION_MODULE")}
          <MultiSelectLiveDropdown
            category={["modules"]}
            language={language}
            isSingleSelect
            filterCallback={(module: any) => !module.is_deprecated}
            toggleClass={`w-100 border ${!selectedModule ? "text-muted" : ""}`}
            label={
              selectedModule?.name ||
              translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PLACEHOLDERS.SELECT_CORRECTION_MODULE")
            }
            onConfirm={(selected) => setSelectedModule(selected?.[0]?.value ?? null)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          {translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PROGRAM")}
          <MultiSelectDropdown
            items={_programItems}
            language={language}
            label={
              selectedProgram?.name ||
              translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PLACEHOLDERS.SELECT_PROGRAM")
            }
            mock={loading.programs}
            isSingleSelect
            badgeItemProperty="name"
            displayItem={(item: any) => <span>{item.name}</span>}
            onConfirm={(items: any[]) => setSelectedProgram(items.length ? items[0] : null)}
            borderToggle
            btnWidth="100%"
            toggleClass="w-100"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          {translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.SUCCESS_RATIO")}
          <Form.Control
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={form.success_ratio}
            placeholder={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.PLACEHOLDERS.SUCCESS_RATIO")}
            onChange={(event) => _handleChange("success_ratio", event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.IS_DEPRECATED")}
            checked={form.is_deprecated}
            onChange={(event) => _handleChange("is_deprecated", event.target.checked)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            label={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.ALLOW_REPASS")}
            checked={form.allow_repass}
            onChange={(event) => _handleChange("allow_repass", event.target.checked)}
          />
        </Form.Group>

        <Modal.Footer>
          <DstButton
            value={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.BUTTONS.CANCEL")}
            variant="light"
            btnClass="me-2"
            clickFunction={() => {
              setShowCreationModal(false);
              _resetSelection();
            }}
          />

          <DstButton
            value={translate(language, "PAGES.EVALUATIONS.CREATION_MODAL.BUTTONS.CREATE")}
            variant="primary"
            loading={loading.creation}
            disabled={loading.creation || _isFormInvalid}
            clickFunction={_handleSubmit}
          />
        </Modal.Footer>
      </Popup>
      <DstAlert show={showAlert} onHide={_handleAlertClose} {...alertProps} />
    </React.Fragment>
  );
};

export default EvaluationCreationModal;
